import R from 'ramda';

import {
  WeeklyTimePair,
  mergeConnectedTimePairs,
  normalizeWeekLongTimePairs,
  weeklyTimePairsToOpeningTimes,
} from 'common/components/weekly-schedules/WeeklyScheduleInput/fromWeeklyScheduleInputEntriesToWeeklySchedule';
import { WeeklySchedule } from 'common/components/weekly-schedules/types';

import { WEEKDAYS } from './constants';
import { TimePairsByWeekday } from './types';

const MINUTE_DURATION_MS = 60 * 1000;
const DAY_DURATION_MS = 24 * 60 * MINUTE_DURATION_MS;
const WEEK_DURATION_MS = 7 * DAY_DURATION_MS;

export const scheduleToWeeklyTimePairs = (weeklySchedule: TimePairsByWeekday): WeeklyTimePair[] => {
  type NormalizedEntry = {
    weekdayIndices: number[];
    timePairs: { start: number; end: number }[];
  };

  const scheduleToNormalizedEntries = (schedule: TimePairsByWeekday): NormalizedEntry[] => {
    return WEEKDAYS.map(weekday => {
      return {
        weekdayIndices: [WEEKDAYS.indexOf(weekday)],
        timePairs: schedule[weekday].map(timePair => ({
          start: timePair.startMinutes * MINUTE_DURATION_MS,
          end: timePair.endMinutes * MINUTE_DURATION_MS,
        })),
      };
    });
  };

  const normalizedEntriesToWeeklyTimePairs = (entries: NormalizedEntry[]) =>
    entries.flatMap(rawEntry => {
      return rawEntry.weekdayIndices.flatMap(weekdayIndex => {
        return rawEntry.timePairs.map(timePair => {
          return {
            start: (timePair.start + weekdayIndex * DAY_DURATION_MS) % WEEK_DURATION_MS,
            end: (timePair.end + weekdayIndex * DAY_DURATION_MS) % WEEK_DURATION_MS,
          };
        });
      });
    });

  return normalizedEntriesToWeeklyTimePairs(scheduleToNormalizedEntries(weeklySchedule));
};

const fromTimePairsByWeekdayToWeeklySchedule: (schedule: TimePairsByWeekday) => WeeklySchedule = R.pipe(
  scheduleToWeeklyTimePairs,
  mergeConnectedTimePairs,
  // If there is a time pair that covers the whole week, make it always start on Monday at 00:00 and end on Sunday at
  // 24:00. Restaurant API does not allow a time span to have the same start and end time, so a time span covering the
  // whole week always has to start and end at midnight. So eg. a time span starting on Wednesday 12:00 and ending on
  // Wednesday 12:00 is not allowed while a time span starting on Wednesday 00:00 and ending on Tuesday 24:00 is ok.
  normalizeWeekLongTimePairs,
  weeklyTimePairsToOpeningTimes,
);

export default fromTimePairsByWeekdayToWeeklySchedule;
